import { SupportErrorCode, SupportErrorMessages } from "./error-codes";

/**
 * Structured error response for frontend consumption.
 * Includes an error code and optional additional details.
 */
export interface FrontendError {
  code: SupportErrorCode;
  message: string;
  details?: string;
}

/**
 * Parameters for creating a SupportError.
 */
export interface SupportErrorParams {
  code: SupportErrorCode;
  frontendMessage?: string;
  sourceError?: Error;
}

/**
 * Error class that can be thrown on the server to represent support-related errors.
 * Includes structured error information that can be serialized and sent to the frontend.
 */
export class SupportError extends Error {
  override name = "SupportError";
  public readonly code: SupportErrorCode;
  public readonly frontendMessage?: string;
  public readonly sourceError?: Error;

  constructor({ code, frontendMessage, sourceError }: SupportErrorParams) {
    super(SupportErrorMessages[code]);
    this.code = code;
    this.frontendMessage = frontendMessage;
    this.sourceError = sourceError;
    // Maintain proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SupportError);
    }
  }

  /**
   * Converts this error to a FrontendError object for serialization.
   */
  toFrontendError(): FrontendError {
    return {
      code: this.code,
      message: this.message,
      details: this.frontendMessage,
    };
  }
}

/**
 * Creates a FrontendError with the appropriate message for the error code.
 */
export function createFrontendError(
  code: SupportErrorCode,
  details?: string,
): FrontendError {
  return { code, message: SupportErrorMessages[code], details };
}

/**
 * Serializes a FrontendError to a JSON string for HTTP response body.
 */
export function serializeFrontendError(error: FrontendError): string {
  return JSON.stringify(error);
}

/**
 * Parses a response body to extract FrontendError if present.
 * Returns null if the response is not a FrontendError.
 */
export function parseFrontendError(
  responseBody: unknown,
): FrontendError | null {
  if (typeof responseBody === "string") {
    try {
      const parsed = JSON.parse(responseBody);
      if (parsed.code && parsed.message) {
        return parsed as FrontendError;
      }
    } catch {
      // Not JSON, not a FrontendError
    }
  } else if (
    typeof responseBody === "object" &&
    responseBody !== null &&
    "code" in responseBody &&
    "message" in responseBody
  ) {
    return responseBody as FrontendError;
  }
  return null;
}

export function parseFrontendErrorMessage(
  responseBody: unknown,
): string | null {
  const frontendError = parseFrontendError(responseBody);
  if (!frontendError) {
    return null;
  }
  return `${frontendError.message}: ${frontendError.details ?? ""}`;
}
